/**
 * Enhanced Search - Universal Predictive Search Script v6
 *
 * This script extends Shopify's predictive search with fuzzy search capabilities.
 * It's designed to work across multiple theme types:
 * - Dawn-based themes (Shopify default)
 * - FoxTheme-based themes (and similar premium themes)
 *
 * Configuration (set via theme extension before this script loads):
 * - window.ENHANCED_SEARCH_ACTIVE: boolean - Enable/disable enhanced search
 * - window.ENHANCED_SEARCH_CLIENT_ID: string - Client ID for the search server
 * - window.ENHANCED_SEARCH_HIGHLIGHT: boolean - Enable/disable search term highlighting
 * - window.ENHANCED_SEARCH_TITLE_PREFIX: string - Optional prefix to remove from product titles
 * - window.ENHANCED_SEARCH_INCLUDE_OUT_OF_STOCK: boolean - Include out-of-stock products
 * - window.ENHANCED_SEARCH_SORT_IN_STOCK_FIRST: boolean - Sort in-stock products first
 * - window.ENHANCED_SEARCH_BOOST_KEYWORDS: string - Comma-separated keywords to boost
 */

// Search server base URL (same for all clients)
const ENHANCED_SEARCH_SERVER_URL = "https://enhanced-search-prod-lmkyt.ondigitalocean.app";

// Configuration with defaults
const ENHANCED_SEARCH_CONFIG = {
  active: typeof window.ENHANCED_SEARCH_ACTIVE === "boolean" ? window.ENHANCED_SEARCH_ACTIVE : false,
  clientId: window.ENHANCED_SEARCH_CLIENT_ID || null,
  highlightEnabled: typeof window.ENHANCED_SEARCH_HIGHLIGHT === "boolean" ? window.ENHANCED_SEARCH_HIGHLIGHT : true,
  titlePrefix: window.ENHANCED_SEARCH_TITLE_PREFIX || "",
  includeOutOfStock:
    typeof window.ENHANCED_SEARCH_INCLUDE_OUT_OF_STOCK === "boolean"
      ? window.ENHANCED_SEARCH_INCLUDE_OUT_OF_STOCK
      : false,
  sortInStockFirst:
    typeof window.ENHANCED_SEARCH_SORT_IN_STOCK_FIRST === "boolean" ? window.ENHANCED_SEARCH_SORT_IN_STOCK_FIRST : true,
  boostKeywords: window.ENHANCED_SEARCH_BOOST_KEYWORDS || "",
};

// Build the full API URL from server URL and client ID
ENHANCED_SEARCH_CONFIG.apiUrl = ENHANCED_SEARCH_CONFIG.clientId
  ? `${ENHANCED_SEARCH_SERVER_URL}/api/mt/${ENHANCED_SEARCH_CONFIG.clientId}`
  : null;

const ENHANCED_SEARCH_DEBOUNCE_MS = 180;
const ENHANCED_SEARCH_MIN_QUERY_LENGTH = 2;

function shouldRunEnhancedSearch(query) {
  return ENHANCED_SEARCH_CONFIG.active && query.trim().length >= ENHANCED_SEARCH_MIN_QUERY_LENGTH;
}

// Validate configuration
if (ENHANCED_SEARCH_CONFIG.active && !ENHANCED_SEARCH_CONFIG.clientId) {
  console.warn(
    "[Enhanced Search] ENHANCED_SEARCH_ACTIVE is true but ENHANCED_SEARCH_CLIENT_ID is not set. Enhanced search will be disabled.",
  );
  ENHANCED_SEARCH_CONFIG.active = false;
}

/**
 * Build the search URL with all query parameters
 */
function buildEnhancedSearchUrl(searchTerm) {
  const params = new URLSearchParams();
  params.set("q", searchTerm);

  if (ENHANCED_SEARCH_CONFIG.includeOutOfStock) {
    params.set("includeOutOfStock", "true");
  }

  if (ENHANCED_SEARCH_CONFIG.sortInStockFirst) {
    params.set("sortInStockFirst", "true");
  }

  if (ENHANCED_SEARCH_CONFIG.boostKeywords) {
    params.set("boostKeywords", ENHANCED_SEARCH_CONFIG.boostKeywords);
  }

  return `${ENHANCED_SEARCH_CONFIG.apiUrl}/search?${params.toString()}`;
}

/**
 * Helper function to highlight matching text in search results
 */
function highlightSearchText(text, query) {
  if (!ENHANCED_SEARCH_CONFIG.highlightEnabled || !query || !text) {
    return text;
  }

  const queryWords = query.trim().split(/\s+/);
  let highlightedText = text;

  queryWords.forEach((word) => {
    if (word.length >= 2) {
      // Reduced from 3 to 2 for better matching
      const escapedWord = word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const regex = new RegExp(`(${escapedWord})`, "gi");
      highlightedText = highlightedText.replace(regex, '<mark class="enhanced-search-highlight">$1</mark>');
    }
  });

  return highlightedText;
}

/**
 * Remove configured title prefix if set
 */
function formatDisplayTitle(title) {
  if (ENHANCED_SEARCH_CONFIG.titlePrefix && title.startsWith(ENHANCED_SEARCH_CONFIG.titlePrefix)) {
    return title.substring(ENHANCED_SEARCH_CONFIG.titlePrefix.length);
  }
  return title;
}

const PRODUCT_TITLE_SELECTORS = [
  ".card__title",
  ".card__heading",
  ".card-product__title",
  ".product-card__title",
  ".predictive-search__item-heading",
  "h3[class*='title']",
  "h4[class*='title']",
  ".card__info h3",
  ".card__info h4",
  ".card__content h3",
  ".card__content h4",
].join(", ");

function formatProductMoney(value) {
  const amount = parseFloat(value) || 0;
  return `$${amount.toFixed(2)}`;
}

function buildProductImageUrl(imageUrl, width = 600) {
  if (!imageUrl) return "";
  const separator = imageUrl.includes("?") ? "&" : "?";
  return `${imageUrl}${separator}width=${width}`;
}

function inferProductMpn(product) {
  if (typeof product.inventory_number === "string" && product.inventory_number.trim()) {
    return product.inventory_number.trim();
  }

  const primarySku = typeof product.sku === "string" ? product.sku.trim() : "";
  if (!primarySku) return "";

  return primarySku.replace(/^[^\d]+/, "").replace(/x\d+$/i, "");
}

function getTitleLinkTarget(titleElement) {
  if (!titleElement) return null;

  const existingTitleLink =
    titleElement.closest("a[href*='/products/']") || titleElement.querySelector("a[href*='/products/']");
  if (existingTitleLink) return existingTitleLink;

  const productContainer =
    titleElement.closest(
      ".card, .card-wrapper, .product-card, .f-column, li, .predictive-search__item, .predictive-search__list-item",
    ) || titleElement.parentElement;

  if (!productContainer) return null;

  return (
    Array.from(productContainer.querySelectorAll("a[href*='/products/']")).find(
      (link) => !titleElement.contains(link),
    ) || null
  );
}

function ensureTitleElementIsLinked(titleElement, productUrl = null, productTitle = null) {
  if (!titleElement) return;

  const targetLink = getTitleLinkTarget(titleElement);
  const resolvedUrl = productUrl || targetLink?.getAttribute("href") || targetLink?.href;
  if (!resolvedUrl) return;

  if (targetLink && (titleElement.closest("a") === targetLink || titleElement.querySelector("a") === targetLink)) {
    targetLink.href = resolvedUrl;
    targetLink.setAttribute("href", resolvedUrl);
    if (productTitle) targetLink.setAttribute("aria-label", productTitle);
    return;
  }

  const titleLink = document.createElement("a");
  titleLink.href = resolvedUrl;
  titleLink.setAttribute("href", resolvedUrl);
  if (productTitle) titleLink.setAttribute("aria-label", productTitle);
  titleLink.className = "full-unstyled-link";
  titleLink.innerHTML = titleElement.innerHTML;
  titleElement.innerHTML = "";
  titleElement.appendChild(titleLink);
}

function ensureProductTitleLinks(root) {
  if (!root) return;

  root.querySelectorAll(PRODUCT_TITLE_SELECTORS).forEach((titleElement) => {
    ensureTitleElementIsLinked(titleElement);
  });
}

function buildFoxThemeFallbackProductCard(product, query) {
  const displayTitle = formatDisplayTitle(product.title);
  const highlightedTitle = highlightSearchText(displayTitle, query);
  const price = parseFloat(product.price) || 0;
  const compareAtPrice = parseFloat(product.compare_at_price) || 0;
  const hasDiscount = compareAtPrice > price;
  const imageUrl = buildProductImageUrl(product.image, 300);

  return `
          <div class="f-column" style="--f-column-span: 2; --f-column-span-mobile: 6;">
            <div class="card card--product">
              <a href="/products/${product.handle}" class="card__link full-width-link">
                <span class="visually-hidden">${product.title}</span>
              </a>
              <div class="card__media">
                <div class="media media--portrait media--hover-effect" style="--media-aspect-ratio: 1">
                  <img src="${imageUrl}" alt="${product.title}" loading="lazy" width="150" height="150" class="motion-reduce">
                </div>
              </div>
              <div class="card__content">
                <div class="card__info">
                  <h3 class="card__title h6"><a href="/products/${product.handle}" class="full-unstyled-link" aria-label="${product.title}">${highlightedTitle}</a></h3>
                  <div class="card__price">
                    <div class="price ${!product.available ? "price--sold-out" : ""} ${hasDiscount ? "price--on-sale" : ""}">
                      <span class="price__regular">${formatProductMoney(product.price)}</span>
                      ${hasDiscount ? `<span class="price__compare"><s>${formatProductMoney(product.compare_at_price)}</s></span>` : ""}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        `;
}

function buildFoxThemeProductCardHtml(templateItem, product, query) {
  if (!templateItem) {
    return buildFoxThemeFallbackProductCard(product, query);
  }

  const clone = templateItem.cloneNode(true);
  clone.removeAttribute("id");
  clone.querySelectorAll("[id]").forEach((element) => element.removeAttribute("id"));

  const productUrl = `/products/${product.handle}`;
  clone.querySelectorAll("a[href*='/products/']").forEach((link) => {
    link.href = productUrl;
    link.setAttribute("href", productUrl);
    link.setAttribute("aria-label", product.title);
  });

  const titleElement = clone.querySelector(PRODUCT_TITLE_SELECTORS);
  if (titleElement) {
    titleElement.innerHTML = highlightSearchText(formatDisplayTitle(product.title), query);

    ensureTitleElementIsLinked(titleElement, productUrl, product.title);
  }

  clone.querySelectorAll("source").forEach((element) => element.remove());
  const imageElements = Array.from(clone.querySelectorAll("img"));
  const primaryImage = imageElements[0];
  if (primaryImage) {
    const imageUrl = buildProductImageUrl(product.image, 600);
    if (imageUrl) {
      primaryImage.src = imageUrl;
      primaryImage.setAttribute("src", imageUrl);
    }
    primaryImage.removeAttribute("srcset");
    primaryImage.removeAttribute("data-src");
    primaryImage.removeAttribute("data-widths");
    primaryImage.setAttribute("alt", product.title);
    primaryImage.alt = product.title;
  }
  imageElements.slice(1).forEach((element) => element.remove());

  const regularPriceItem = clone.querySelector(".price-item--regular");
  if (regularPriceItem) {
    regularPriceItem.textContent = formatProductMoney(product.price);
  } else {
    const regularPriceContainer = clone.querySelector(".price__regular");
    if (regularPriceContainer) {
      regularPriceContainer.innerHTML = `<span class="price-item price-item--regular">${formatProductMoney(product.price)}</span>`;
    }
  }

  const hasDiscount = (parseFloat(product.compare_at_price) || 0) > (parseFloat(product.price) || 0);
  const comparePriceWrapper = clone.querySelector(".price__compare, .price-item--sale, .price-item--compare");
  if (hasDiscount && comparePriceWrapper) {
    const compareTarget = comparePriceWrapper.querySelector("s") || comparePriceWrapper;
    compareTarget.textContent = formatProductMoney(product.compare_at_price);
    comparePriceWrapper.classList.remove("hidden");
  } else if (comparePriceWrapper) {
    comparePriceWrapper.remove();
  }

  const priceWrapper = clone.querySelector(".price");
  if (priceWrapper) {
    priceWrapper.classList.toggle("price--on-sale", hasDiscount);
    priceWrapper.classList.toggle("price--sold-out", !product.available);
  }

  const metadataElement = Array.from(clone.querySelectorAll("*"))
    .filter((element) => {
      const text = (element.textContent || "").replace(/\s+/g, " ").trim();
      return text.length > 0 && text.length < 160 && (text.includes("SKU #") || text.includes("MPN #"));
    })
    .sort((a, b) => a.textContent.length - b.textContent.length)[0];
  if (metadataElement) {
    const sku = typeof product.sku === "string" ? product.sku.trim() : "";
    const mpn = inferProductMpn(product);
    metadataElement.innerHTML = `SKU #:<br>${sku}${mpn ? ` | <br>MPN #: ${mpn}` : ""}`;
  }

  const availabilityElement = Array.from(clone.querySelectorAll("*"))
    .filter((element) => {
      const text = (element.textContent || "").replace(/\s+/g, " ").trim().toLowerCase();
      return (
        text.length > 0 &&
        text.length < 160 &&
        (text.includes("available in stock") || text.includes("special order") || text.includes("out of stock"))
      );
    })
    .sort((a, b) => a.textContent.length - b.textContent.length)[0];
  if (availabilityElement) {
    availabilityElement.innerHTML = product.available
      ? "Available in<br>stock"
      : "Special Order.<br>Expect 4-6 weeks<br>for delivery,<br>subject to change";
  }

  return clone.outerHTML;
}

/**
 * Detect theme type based on available globals and DOM structure
 */
function detectThemeType() {
  if (typeof FoxTheme !== "undefined" && FoxTheme.routes) {
    return "foxtheme";
  }
  if (typeof SearchForm !== "undefined") {
    return "dawn";
  }
  // Check for common Dawn elements
  if (document.querySelector("[data-predictive-search]")) {
    return "dawn";
  }
  // Default to foxtheme-style if we detect their structure
  if (document.querySelector(".search__content")) {
    return "foxtheme";
  }
  return "unknown";
}

const THEME_TYPE = detectThemeType();
console.log(`[Enhanced Search] Detected theme type: ${THEME_TYPE}`);

// ============================================
// FoxTheme-based Implementation
// ============================================
if (THEME_TYPE === "foxtheme" || THEME_TYPE === "unknown") {
  // Inject the spinner keyframes once. CSS animation on the SVG element gives a
  // clean center-pivot rotation; SMIL animateTransform on the inner <circle>
  // doesn't reliably rotate around the SVG's center across all browsers.
  if (typeof document !== "undefined" && !document.getElementById("enhanced-search-styles")) {
    const style = document.createElement("style");
    style.id = "enhanced-search-styles";
    style.textContent = `
      @keyframes enhanced-search-spin {
        to { transform: rotate(360deg); }
      }
      .enhanced-search-spinner {
        animation: enhanced-search-spin 0.9s linear infinite;
        transform-origin: center center;
        transform-box: fill-box;
      }
    `;
    document.head.appendChild(style);
  }

  // Module-scoped template cache: persists across instances and across searches.
  // Captured from the first Shopify response that includes a rendered products column,
  // then reused whenever Shopify returns zero products but we have enhanced results.
  const foxThemeTemplateCache = {
    productsColumn: null, // Outer .search__box-products column (cloned from Shopify response)
    productCard: null, // A single product card element to clone for enhanced products
    outerWrapper: null, // The flex wrapper class string (e.g., "flex w-full flex-col gap-y-6 md:flex-row")
  };

  function captureFoxThemeTemplates(renderedRoot) {
    if (!renderedRoot) return;

    // Cache a single product card. Look in the predictive-search results grid first,
    // then fall back to any .f-grid inside a .search__box-products column — this also
    // matches the "Most Searched Products" recommendations block, which uses the same
    // card-product snippet and is rendered into the dropdown DOM at page load.
    if (!foxThemeTemplateCache.productCard) {
      const productsList =
        renderedRoot.querySelector("#predictive-search-results-products-list") ||
        renderedRoot.querySelector(".search__box-products .f-grid");
      const firstCard = productsList?.children[0];
      if (firstCard) {
        foxThemeTemplateCache.productCard = firstCard.cloneNode(true);
      }
    }

    // Cache the predictive-search results column structure (heading, "Search for X" button,
    // grid with theme spacing classes). Exclude the recommendations column — its heading
    // and structure differ from what we want to render for search results.
    if (!foxThemeTemplateCache.productsColumn) {
      const productsColumn = renderedRoot.querySelector(
        ".search__box-products:not(.search__products-recommendation):not(.search__products-recommendation *)",
      );
      if (productsColumn) {
        foxThemeTemplateCache.productsColumn = productsColumn.cloneNode(true);
        const wrapper = productsColumn.parentElement;
        if (wrapper && !foxThemeTemplateCache.outerWrapper) {
          foxThemeTemplateCache.outerWrapper = wrapper.className || "flex w-full flex-col gap-y-6 md:flex-row";
        }
      }
    }
  }

  /**
   * Warm the template cache before the user's first search so enhanced-only
   * results have proper styling on the very first query of a session.
   *
   * Strategy:
   *   1. Scan the predictive-search element's existing DOM for cards (e.g., the
   *      "Most Searched Products" recommendations block). Free, instant, no fetch.
   *   2. If no cards found, fire one Shopify predictive-search request with a
   *      common letter that's almost guaranteed to match products. Parse the
   *      response into the template cache without rendering anything.
   *
   * This is fire-and-forget. If it fails, the cold-start fallback markup still works.
   */
  async function warmFoxThemeTemplateCache(predictiveSearchElement) {
    if (foxThemeTemplateCache.productCard && foxThemeTemplateCache.productsColumn) return;

    // Step 1: scan local DOM (recommendations block lives inside <predictive-search>)
    captureFoxThemeTemplates(predictiveSearchElement);
    if (foxThemeTemplateCache.productCard && foxThemeTemplateCache.productsColumn) return;

    // Step 2: fire a throwaway Shopify predictive search to populate any missing slots
    try {
      const sectionId =
        predictiveSearchElement.getSectionId?.() ||
        predictiveSearchElement.querySelector("form[id^='PredictiveSearch-']")?.id?.replace("PredictiveSearch-", "") ||
        "main";
      const baseUrl =
        typeof FoxTheme !== "undefined" && FoxTheme.routes?.predictive_search_url
          ? FoxTheme.routes.predictive_search_url
          : "/search/suggest";
      const url = new URL((window.shopUrl || "") + baseUrl, window.location.origin);
      url.searchParams.set("q", "a");
      url.searchParams.set("resources[limit]", "4");
      url.searchParams.set("resources[limit_scope]", "each");
      url.searchParams.set("section_id", sectionId);

      const response = await fetch(url.toString());
      if (!response.ok) return;
      const html = await response.text();
      const parser = new DOMParser();
      const parsedDoc = parser.parseFromString(html, "text/html");
      const contentElement = parsedDoc.getElementById(`PredictiveSearchResults-${sectionId}`);
      if (contentElement) {
        captureFoxThemeTemplates(contentElement);
      }
    } catch (e) {
      // Silent fail — hand-coded fallback in buildProductsColumnFromTemplate still works
      console.debug("[Enhanced Search] Template cache warm-up failed:", e);
    }
  }

  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.cachedMap = new Map();
      this.shopifyCache = new Map();
      this.enhancedCache = new Map();
      this.requestSequence = 0;
      this.activeFetchController = null;
      this.focusElement = this.input;
      this.resetButton.addEventListener("click", this.clear.bind(this));
      this.input.addEventListener("input", this.debounce(this.onChange.bind(this), ENHANCED_SEARCH_DEBOUNCE_MS));
      this.input.addEventListener("focus", this.onFocus.bind(this));
      this.searchContent = this.querySelector(".search__content");
      this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === "true";
      this.header = document.querySelector("header");
      this.toggleNavigationButton = document.querySelector(".toggle-navigation-button");

      this.boundHandleClickOutside = this.handleClickOutside.bind(this);
      document.addEventListener("click", this.boundHandleClickOutside);
      this.searchProductTypes?.addEventListener("change", this.handleProductTypeChange.bind(this));

      this.isHeaderSearch = this.closest("header") !== null;

      document.addEventListener("menu-drawer:open", () => {
        this.classList.remove("predictive-search-open");
        document.body.classList.remove("search-open");
      });

      this.states = {
        OPEN: "predictive-search-open",
        LOADING: "btn--loading",
        SEARCH_OPEN: "search-open",
      };

      // Pre-warm the template cache so the first enhanced-only search has correct
      // FoxTheme styling, even before any Shopify-with-products response is observed.
      // Run after the current microtask so the constructor finishes synchronously.
      this.warmTemplateCachePromise = Promise.resolve().then(() => warmFoxThemeTemplateCache(this));
    }

    // Simple debounce implementation
    debounce(fn, delay) {
      let timeoutId;
      return (...args) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => fn.apply(this, args), delay);
      };
    }

    // Get section ID from element
    getSectionId() {
      // Try FoxTheme utility first
      if (typeof FoxTheme !== "undefined" && FoxTheme.utils?.getSectionId) {
        return FoxTheme.utils.getSectionId(this);
      }
      // Fallback: extract from form ID or generate from element
      const form = this.querySelector("form");
      if (form && form.id) {
        const match = form.id.match(/PredictiveSearch-(.+)/);
        if (match) return match[1];
      }
      // Last resort: use a hash of the element
      return this.id || "main";
    }

    get input() {
      return this.querySelector('input[type="search"]');
    }

    get resetButton() {
      return this.querySelector('button[type="reset"]');
    }

    get searchProductTypes() {
      return this.querySelector("#SearchProductTypes");
    }

    onFocus(event) {
      document.documentElement.style.setProperty("--viewport-height", `${window.innerHeight}px`);
      if (this.header) {
        document.documentElement.style.setProperty(
          "--header-bottom-position",
          `${parseInt(this.header.getBoundingClientRect().bottom)}px`,
        );
      }
      if (this.isHeaderSearch && !this.searchRecommendationEmpty) {
        document.body.classList.add("search-open");
        this.toggleNavButtonVisibility(false);
      }

      if (!this.searchRecommendationEmpty) {
        this.searchContent?.classList.remove("hidden");
      }
      this.classList.add("predictive-search-open");
      if (this.getQuery().length === 0) {
        if (this.searchRecommendationEmpty) {
          this.searchContent?.classList.add("hidden");
        }
        return;
      }
      this.performSearch();
    }

    getQuery() {
      return this.input?.value.trim() || "";
    }

    clear(event = null) {
      event?.preventDefault();
      this.activeFetchController?.abort();
      this.activeFetchController = null;
      this.input.value = "";
      this.input.focus();
      this.removeAttribute("results");
      this.toggleSearchState(false);
    }

    isStaleRequest(requestId, cacheKey) {
      return requestId !== this.requestSequence || this.getQuery().toLowerCase() !== cacheKey;
    }

    handleProductTypeChange(evt) {
      const query = this.getQuery();
      if (query.length > 0) {
        this.performSearch();
      }
    }

    getShopifySearchUrl() {
      // Try FoxTheme routes first
      const baseUrl =
        typeof FoxTheme !== "undefined" && FoxTheme.routes?.predictive_search_url
          ? FoxTheme.routes.predictive_search_url
          : "/search/suggest";

      const shopUrl = window.shopUrl || "";
      const url = new URL(`${shopUrl}${baseUrl}`, window.location.origin);

      let search_term = this.getQuery();
      if (this.searchProductTypes && this.searchProductTypes.value !== "") {
        search_term = `product_type:${this.searchProductTypes.value} AND ${encodeURIComponent(search_term)}`;
      }

      url.searchParams.set("q", search_term);
      url.searchParams.set("resources[limit]", this.dataset.resultsLimit || "6");
      url.searchParams.set("resources[limit_scope]", "each");
      url.searchParams.set("section_id", this.getSectionId());

      return url.toString();
    }

    onChange() {
      if (this.getQuery().length === 0) {
        this.clear();
        return;
      }
      this.performSearch();
    }

    async performSearch() {
      const query = this.getQuery();
      const cacheKey = query.toLowerCase();

      if (this.cachedMap.has(cacheKey)) {
        this.renderSearchResults(this.cachedMap.get(cacheKey));
        return;
      }

      this.activeFetchController?.abort();
      this.activeFetchController = new AbortController();
      const { signal } = this.activeFetchController;
      const requestId = ++this.requestSequence;

      this.setLoadingState(true);

      try {
        const shopifyUrl = this.getShopifySearchUrl();
        const shopifyPromise = this.shopifyCache.has(cacheKey)
          ? Promise.resolve(this.shopifyCache.get(cacheKey))
          : fetch(shopifyUrl, { signal }).then((response) => response.text());

        // Give the template warm-up a brief window to finish before we may need it
        // for the merge step. Bounded so we never block the user perceptibly.
        if (this.warmTemplateCachePromise && !foxThemeTemplateCache.productCard) {
          await Promise.race([
            this.warmTemplateCachePromise,
            new Promise((resolve) => setTimeout(resolve, 400)),
          ]);
        }

        const enhancedPromise = shouldRunEnhancedSearch(query)
          ? this.enhancedCache.has(cacheKey)
            ? Promise.resolve(this.enhancedCache.get(cacheKey))
            : fetch(buildEnhancedSearchUrl(query), {
                signal,
                headers: { Accept: "application/json" },
              }).then((response) => response.json())
          : null;

        const shopifyText = await shopifyPromise;
        this.shopifyCache.set(cacheKey, shopifyText);

        if (this.isStaleRequest(requestId, cacheKey)) {
          return;
        }

        // If Shopify returned an empty state AND we're still waiting on enhanced
        // results, don't paint the "no results" message — it'd flash and then get
        // replaced when enhanced products arrive. Show a loading placeholder instead
        // and defer rendering until enhanced resolves.
        const shopifyIsEmpty = enhancedPromise && this.isShopifyEmptyResponse(shopifyText);
        if (!shopifyIsEmpty) {
          this.renderSearchResults(shopifyText);
          this.setLoadingState(false);
        } else {
          this.renderLoadingPlaceholder(query);
          // Keep loading state on the reset button — enhanced is still running.
        }

        if (!enhancedPromise) {
          this.cachedMap.set(cacheKey, shopifyText);
          return;
        }

        const fuzzyProducts = await enhancedPromise.catch((error) => {
          console.warn("[Enhanced Search] Enhanced fetch failed, using Shopify-only results:", error);
          return null;
        });

        if (this.isStaleRequest(requestId, cacheKey)) {
          return;
        }

        if (!fuzzyProducts || !fuzzyProducts.products || fuzzyProducts.products.length === 0) {
          // Enhanced gave us nothing usable. If we deferred rendering Shopify's
          // empty state, render it now so the user sees the proper "no results" UI.
          if (shopifyIsEmpty) {
            this.renderSearchResults(shopifyText);
          }
          this.setLoadingState(false);
          this.cachedMap.set(cacheKey, shopifyText);
          return;
        }

        this.enhancedCache.set(cacheKey, fuzzyProducts);

        const enhancedHtml = this.mergeSearchResults(shopifyText, fuzzyProducts, query);
        this.cachedMap.set(cacheKey, enhancedHtml);
        this.renderSearchResults(enhancedHtml);
        this.setLoadingState(false);
      } catch (error) {
        if (error?.name === "AbortError" || error?.code === 20) {
          return;
        }

        console.error("[Enhanced Search] Error:", error);
        this.setLoadingState(false);
        this.setAttribute("error", "Failed to load data");
      } finally {
        if (this.activeFetchController?.signal === signal) {
          this.activeFetchController = null;
        }
      }
    }

    /**
     * Detect whether a Shopify predictive-search response is the "no results"
     * empty state — i.e., it has no products column and nothing else useful.
     * Used to defer painting the empty state when enhanced results may still arrive.
     */
    isShopifyEmptyResponse(shopifyHtml) {
      const sectionId = this.getSectionId();
      const parser = new DOMParser();
      const doc = parser.parseFromString(shopifyHtml, "text/html");
      const target = doc.getElementById(`PredictiveSearchResults-${sectionId}`);
      if (!target) return false;

      const hasProducts =
        target.querySelector("#predictive-search-results-products-list") ||
        target.querySelector(".search__box-products .f-grid");
      const hasSidebar = target.querySelector(".search__box-others");

      // Empty when there's no products column and no sidebar (queries/collections/articles).
      // We still defer if there's an explicit .empty-state element with nothing else.
      return !hasProducts && !hasSidebar;
    }

    /**
     * Render a lightweight loading placeholder into the dropdown's results target
     * while we wait on the enhanced search server. Reuses FoxTheme's empty-state
     * structure so the dropdown layout/spacing stays consistent.
     */
    renderLoadingPlaceholder(query) {
      const sectionId = this.getSectionId();
      const target = document.getElementById(`PredictiveSearchResults-${sectionId}`);
      if (!target) return;

      this.searchContent?.classList.remove("hidden");
      this.classList.add("predictive-search-open");
      if (this.isHeaderSearch && !document.body.classList.contains("search-open")) {
        document.body.classList.add("search-open");
        this.toggleNavButtonVisibility(true);
      }

      const safeQuery = (query || "").replace(/[<>&"]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", '"': "&quot;" })[c]);
      target.innerHTML = `
        <div class="empty-state flex flex-col items-center justify-center gap-3 w-full h-full" data-enhanced-search-loading="true">
          <svg class="enhanced-search-spinner" viewBox="0 0 50 50" width="36" height="36" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" style="opacity:0.6;">
            <circle cx="25" cy="25" r="20" fill="none" stroke="currentColor" stroke-width="4" stroke-linecap="round" stroke-dasharray="80 45"/>
          </svg>
          <p class="m-0 text-subtext">Searching for &ldquo;${safeQuery}&rdquo;&hellip;</p>
        </div>
      `;
    }

    mergeSearchResults(shopifyHtml, fuzzyData, query) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(shopifyHtml, "text/html");
      const sectionId = this.getSectionId();
      const resultsId = `PredictiveSearchResults-${sectionId}`;
      const targetElement = doc.getElementById(resultsId);

      if (!targetElement || !fuzzyData.products || fuzzyData.products.length === 0) {
        return shopifyHtml;
      }

      // Capture template structures from this response if Shopify returned any.
      captureFoxThemeTemplates(targetElement);

      let productsList =
        targetElement.querySelector("#predictive-search-results-products-list") ||
        targetElement.querySelector(".predictive-search-result--products .f-grid") ||
        targetElement.querySelector(".search__box-products .f-grid");

      // ----- Case A: Shopify returned a products column — prepend fuzzy results into it.
      if (productsList) {
        const existingItems = Array.from(productsList.children);
        const existingHandles = new Set();
        productsList.querySelectorAll("a[href*='/products/']").forEach((link) => {
          const match = link.href.match(/\/products\/([^/?]+)/);
          if (match) existingHandles.add(match[1]);
        });

        const uniqueFuzzyProducts = fuzzyData.products.filter((p) => !existingHandles.has(p.handle));
        if (uniqueFuzzyProducts.length === 0) {
          return shopifyHtml;
        }

        const templateItem = existingItems[0] || foxThemeTemplateCache.productCard || null;
        const fuzzyHtml = uniqueFuzzyProducts
          .map((product) => buildFoxThemeProductCardHtml(templateItem, product, query))
          .join("");
        productsList.insertAdjacentHTML("afterbegin", fuzzyHtml);

        if (ENHANCED_SEARCH_CONFIG.highlightEnabled) {
          existingItems.forEach((item) => {
            item.querySelectorAll(PRODUCT_TITLE_SELECTORS).forEach((el) => {
              const text = el.textContent;
              if (text) el.innerHTML = highlightSearchText(text, query);
            });
          });
        }
        return doc.body.innerHTML;
      }

      // ----- Case B: Shopify returned NO products column.
      // Goal: keep whatever Shopify did render (sidebar + empty state banner), inject
      // a products column built from the cached theme-rendered template so our results
      // inherit the exact FoxTheme styling.

      // Preserve the sidebar column if present (queries / collections / articles-pages).
      const existingSidebar = targetElement.querySelector(".search__box-others");
      // Preserve the "no results for X" empty-state banner if Shopify rendered one.
      const existingEmptyState = targetElement.querySelector(".empty-state");

      const productsColumn = this.buildProductsColumnFromTemplate(fuzzyData.products, query, sectionId);
      if (!productsColumn) {
        return shopifyHtml;
      }

      // Clear the target and rebuild with: products column + (preserved sidebar if any).
      targetElement.innerHTML = "";

      const wrapper = document.createElement("div");
      wrapper.className = foxThemeTemplateCache.outerWrapper || "flex w-full flex-col gap-y-6 md:flex-row";
      wrapper.appendChild(productsColumn);
      if (existingSidebar) {
        wrapper.appendChild(existingSidebar);
      }
      targetElement.appendChild(wrapper);

      // If Shopify showed "no results for X" but we're filling with fuzzy matches,
      // drop the banner — it'd contradict the results we're about to show.
      if (existingEmptyState && !existingSidebar) {
        existingEmptyState.remove();
      }

      return doc.body.innerHTML;
    }

    /**
     * Build a products column populated with enhanced-search results.
     * Prefers cloning the cached theme-rendered column (exact visual parity);
     * falls back to a minimal structure if we've never seen a successful Shopify render.
     */
    buildProductsColumnFromTemplate(products, query, sectionId) {
      if (!products || products.length === 0) return null;

      const cachedColumn = foxThemeTemplateCache.productsColumn;
      const cardTemplate = foxThemeTemplateCache.productCard;

      if (cachedColumn) {
        const column = cachedColumn.cloneNode(true);
        const grid =
          column.querySelector("#predictive-search-results-products-list") || column.querySelector(".f-grid");
        if (grid) {
          grid.innerHTML = products
            .map((product) => buildFoxThemeProductCardHtml(cardTemplate, product, query))
            .join("");
        }
        // Update the "Search for X" button's label/form binding to the current query/section.
        const searchForButton = column.querySelector(".search__results-all button, .search__results-all a");
        if (searchForButton) {
          if (searchForButton.tagName === "BUTTON") {
            searchForButton.setAttribute("form", `PredictiveSearch-${sectionId}`);
          }
          const label = searchForButton.querySelector(".btn__text");
          if (label) {
            const textNode = Array.from(label.childNodes).find((n) => n.nodeType === Node.TEXT_NODE);
            if (textNode) {
              textNode.textContent = textNode.textContent.replace(/"[^"]*"/, `"${query}"`);
            }
          }
        }
        return column;
      }

      // Fallback: no cached template yet. Build a reasonable approximation.
      const column = document.createElement("div");
      column.className = "flex flex-col flex-grow search__box-products order-last md:order-first";
      column.innerHTML = `
        <div class="flex flex-col gap-5 lg:gap-6 predictive-search-result search__box-item predictive-search-result--products">
          <h4 class="h4 predictive-search-result__heading">Products</h4>
          <div class="swipe-mobile swipe-mobile--2-cols">
            <div id="predictive-search-results-products-list" class="f-grid f-grid--gap-4 f-grid--row-gap-6 swipe-mobile__inner">
              ${products.map((p) => buildFoxThemeProductCardHtml(cardTemplate, p, query)).join("")}
            </div>
          </div>
          <div class="search__results-all block">
            <button type="submit" class="btn btn--primary" form="PredictiveSearch-${sectionId}">
              <span class="btn__text">Search for "${query}"</span>
            </button>
          </div>
        </div>
      `;
      return column;
    }

    renderSearchResults(responseText) {
      const sectionId = this.getSectionId();
      const id = `PredictiveSearchResults-${sectionId}`;
      const targetElement = document.getElementById(id);

      if (targetElement) {
        const parser = new DOMParser();
        const parsedDoc = parser.parseFromString(responseText, "text/html");
        const contentElement = parsedDoc.getElementById(id);

        if (contentElement) {
          this.searchContent?.classList.remove("hidden");
          targetElement.innerHTML = contentElement.innerHTML;
          ensureProductTitleLinks(targetElement);
          captureFoxThemeTemplates(targetElement);

          if (this.isHeaderSearch && !document.body.classList.contains("search-open")) {
            document.body.classList.add("search-open");
            this.toggleNavButtonVisibility(true);
            this.classList.add("predictive-search-open");
          } else {
            this.classList.add("predictive-search-open");
          }
        }
      }
    }

    handleClickOutside(event) {
      const target = event.target;
      const shouldClose = this.isHeaderSearch
        ? !this.contains(target) &&
          ((target.classList.contains("fixed-overlay") && target.closest(".header-section")) ||
            target.classList.contains("header__search-close"))
        : !this.contains(target);

      if (shouldClose) {
        setTimeout(() => this.toggleSearchState(false));
      }
    }

    toggleSearchState(isOpen) {
      this.classList.toggle(this.states.OPEN, isOpen);
      if (this.isHeaderSearch) {
        document.body.classList.toggle(this.states.SEARCH_OPEN, isOpen);
        this.toggleNavButtonVisibility(!isOpen);
      }
      if (!isOpen && this.searchRecommendationEmpty) {
        this.searchContent?.classList.add("hidden");
      }
    }

    toggleNavButtonVisibility(isOpen) {
      this.toggleNavigationButton?.classList.toggle("is-show", isOpen);
    }

    setLoadingState(isLoading) {
      if (isLoading) {
        this.setAttribute("loading", "true");
        this.resetButton?.classList.add(this.states.LOADING);
      } else {
        this.removeAttribute("loading");
        this.resetButton?.classList.remove(this.states.LOADING);
        this.setAttribute("results", "true");
      }
    }
  }

  customElements.define("predictive-search", PredictiveSearch);
}

// ============================================
// Dawn-based Implementation (original)
// ============================================
else if (THEME_TYPE === "dawn") {
  class PredictiveSearch extends SearchForm {
    constructor() {
      super();
      this.cachedResults = {};
      this.predictiveSearchResults = this.querySelector("[data-predictive-search]");
      this.allPredictiveSearchInstances = document.querySelectorAll("predictive-search");
      this.isOpen = false;
      this.abortController = new AbortController();
      this.searchTerm = "";

      this.setupEventListeners();
    }

    setupEventListeners() {
      this.input.form.addEventListener("submit", this.onFormSubmit.bind(this));
      this.input.addEventListener("focus", this.onFocus.bind(this));
      this.addEventListener("focusout", this.onFocusOut.bind(this));
      this.addEventListener("keyup", this.onKeyup.bind(this));
      this.addEventListener("keydown", this.onKeydown.bind(this));
    }

    getQuery() {
      return this.input.value.trim();
    }

    onChange() {
      super.onChange();
      const newSearchTerm = this.getQuery();
      if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
        this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
      }

      this.updateSearchForTerm(this.searchTerm, newSearchTerm);
      this.searchTerm = newSearchTerm;

      if (!this.searchTerm.length) {
        this.close(true);
        return;
      }

      this.getSearchResults(this.searchTerm);
    }

    onFormSubmit(event) {
      if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
        event.preventDefault();
      }
    }

    onFormReset(event) {
      super.onFormReset(event);
      if (super.shouldResetForm()) {
        this.searchTerm = "";
        this.abortController.abort();
        this.abortController = new AbortController();
        this.closeResults(true);
      }
    }

    onFocus() {
      const currentSearchTerm = this.getQuery();

      if (!currentSearchTerm.length) return;

      if (this.searchTerm !== currentSearchTerm) {
        this.onChange();
      } else if (this.getAttribute("results") === "true") {
        this.open();
      } else {
        this.getSearchResults(this.searchTerm);
      }
    }

    onFocusOut() {
      setTimeout(() => {
        if (!this.contains(document.activeElement)) this.close();
      });
    }

    onKeyup(event) {
      if (!this.getQuery().length) this.close(true);
      event.preventDefault();

      switch (event.code) {
        case "ArrowUp":
          this.switchOption("up");
          break;
        case "ArrowDown":
          this.switchOption("down");
          break;
        case "Enter":
          this.selectOption();
          break;
      }
    }

    onKeydown(event) {
      if (event.code === "ArrowUp" || event.code === "ArrowDown") {
        event.preventDefault();
      }
    }

    updateSearchForTerm(previousTerm, newTerm) {
      const searchForTextElement = this.querySelector("[data-predictive-search-search-for-text]");
      const currentButtonText = searchForTextElement?.innerText;
      if (currentButtonText) {
        if (currentButtonText.match(new RegExp(previousTerm, "g"))?.length > 1) {
          return;
        }
        const newButtonText = currentButtonText.replace(previousTerm, newTerm);
        searchForTextElement.innerText = newButtonText;
      }
    }

    switchOption(direction) {
      if (!this.getAttribute("open")) return;

      const moveUp = direction === "up";
      const selectedElement = this.querySelector('[aria-selected="true"]');

      const allVisibleElements = Array.from(this.querySelectorAll("li, button.predictive-search__item")).filter(
        (element) => element.offsetParent !== null,
      );
      let activeElementIndex = 0;

      if (moveUp && !selectedElement) return;

      let selectedElementIndex = -1;
      let i = 0;

      while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
        if (allVisibleElements[i] === selectedElement) {
          selectedElementIndex = i;
        }
        i++;
      }

      this.statusElement.textContent = "";

      if (!moveUp && selectedElement) {
        activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
      } else if (moveUp) {
        activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
      }

      if (activeElementIndex === selectedElementIndex) return;

      const activeElement = allVisibleElements[activeElementIndex];

      activeElement.setAttribute("aria-selected", true);
      if (selectedElement) selectedElement.setAttribute("aria-selected", false);

      this.input.setAttribute("aria-activedescendant", activeElement.id);
    }

    selectOption() {
      const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');
      if (selectedOption) selectedOption.click();
    }

    async getSearchResults(searchTerm) {
      const queryKey = searchTerm.replace(" ", "-").toLowerCase();
      this.setLiveRegionLoadingState();

      if (this.cachedResults[queryKey]) {
        this.renderSearchResults(this.cachedResults[queryKey]);
        return;
      }

      try {
        const requests = [
          fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
            signal: this.abortController.signal,
          }),
        ];

        if (ENHANCED_SEARCH_CONFIG.active) {
          requests.push(
            fetch(buildEnhancedSearchUrl(searchTerm), {
              signal: this.abortController.signal,
              headers: { Accept: "application/json" },
            }),
          );
        }

        const responses = await Promise.all(requests);

        const shopifyText = await responses[0].text();
        const fuzzyProducts = ENHANCED_SEARCH_CONFIG.active ? await responses[1].json() : { products: [] };

        const parser = new DOMParser();
        const doc = parser.parseFromString(shopifyText, "text/html");
        const resultsContainer = doc.querySelector("#shopify-section-predictive-search");

        const productsList = resultsContainer?.querySelector("#predictive-search-results-products-list");

        if (productsList && fuzzyProducts.products && fuzzyProducts.products.length > 0) {
          const existingItems = Array.from(productsList.children);
          const shopifyProductHandles = new Set();

          existingItems.forEach((item) => {
            const linkElement = item.querySelector("a");
            if (linkElement && linkElement.href) {
              const url = new URL(linkElement.href, window.location.origin);
              const pathSegments = url.pathname.split("/");
              if (pathSegments.length >= 3 && pathSegments[1] === "products") {
                shopifyProductHandles.add(pathSegments[2]);
              }
            }
          });

          const uniqueFuzzyProducts = fuzzyProducts.products.filter(
            (product) => !shopifyProductHandles.has(product.handle),
          );

          const fuzzyResultsHTML = uniqueFuzzyProducts
            .map((product, index) => {
              const displayTitle = formatDisplayTitle(product.title);
              const highlightedTitle = highlightSearchText(displayTitle, searchTerm);
              const price = parseFloat(product.price) || 0;
              const priceMin = parseFloat(product.price_min) || 0;
              const priceMax = parseFloat(product.price_max) || 0;
              const compareAtPrice = parseFloat(product.compare_at_price) || 0;

              return `
              <li id="predictive-search-option-product-${index + 1}" class="predictive-search__list-item" role="option" aria-selected="false">
                <a href="/products/${product.handle}" class="predictive-search__item predictive-search__item--link-with-thumbnail link link--text" tabindex="-1">
                  <img class="predictive-search__image" loading="lazy"
                       src="${product.image}&width=150"
                       width="56" height="56"
                       alt="${product.title}">
                  <div class="predictive-search__item-content">
                    <p class="predictive-search__item-heading h5">
                      ${highlightedTitle}
                    </p>
                    <div class="price ${!product.available ? "price--sold-out" : ""} ${compareAtPrice > price ? "price--on-sale" : ""}">
                      <div class="price__container">
                        <div class="price__regular">
                          <span class="visually-hidden visually-hidden--inline">Regular price</span>
                          <span class="price-item price-item--regular">
                            ${priceMin < priceMax ? '<span class="text--small subdued">From</span> ' : ""}
                            $${price}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </a>
              </li>
            `;
            })
            .join("");

          const updatedExistingItemsHTML = existingItems
            .map((item, index) => {
              const clone = item.cloneNode(true);
              clone.id = `predictive-search-option-product-${index + uniqueFuzzyProducts.length + 1}`;

              if (ENHANCED_SEARCH_CONFIG.highlightEnabled) {
                const titleElement = clone.querySelector(".predictive-search__item-heading");
                if (titleElement) {
                  const text = titleElement.textContent;
                  if (text) {
                    titleElement.innerHTML = highlightSearchText(text, searchTerm);
                  }
                }
              }

              return clone.outerHTML;
            })
            .join("");

          productsList.innerHTML = fuzzyResultsHTML + updatedExistingItemsHTML;
        }

        const resultsMarkup = resultsContainer?.innerHTML || shopifyText;

        this.allPredictiveSearchInstances.forEach((instance) => {
          instance.cachedResults[queryKey] = resultsMarkup;
        });

        this.renderSearchResults(resultsMarkup);
      } catch (error) {
        if (error?.code === 20) return;
        this.close();
        throw error;
      }
    }

    setLiveRegionLoadingState() {
      this.statusElement = this.statusElement || this.querySelector(".predictive-search-status");
      this.loadingText = this.loadingText || this.getAttribute("data-loading-text");
      this.setLiveRegionText(this.loadingText);
      this.setAttribute("loading", true);
    }

    setLiveRegionText(statusText) {
      this.statusElement?.setAttribute("aria-hidden", "false");
      if (this.statusElement) this.statusElement.textContent = statusText;

      setTimeout(() => {
        this.statusElement?.setAttribute("aria-hidden", "true");
      }, 1000);
    }

    renderSearchResults(resultsMarkup) {
      this.predictiveSearchResults.innerHTML = resultsMarkup;
      this.setAttribute("results", true);
      this.setLiveRegionResults();
      this.open();
    }

    setLiveRegionResults() {
      this.removeAttribute("loading");
      const countElement = this.querySelector("[data-predictive-search-live-region-count-value]");
      if (countElement) {
        this.setLiveRegionText(countElement.textContent);
      }
    }

    getResultsMaxHeight() {
      const header = document.querySelector(".section-header");
      this.resultsMaxHeight = header
        ? window.innerHeight - header.getBoundingClientRect().bottom
        : window.innerHeight - 100;
      return this.resultsMaxHeight;
    }

    open() {
      this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
      this.setAttribute("open", true);
      this.input.setAttribute("aria-expanded", true);
      this.isOpen = true;
    }

    close(clearSearchTerm = false) {
      this.closeResults(clearSearchTerm);
      this.isOpen = false;
    }

    closeResults(clearSearchTerm = false) {
      if (clearSearchTerm) {
        this.input.value = "";
        this.removeAttribute("results");
      }
      const selected = this.querySelector('[aria-selected="true"]');

      if (selected) selected.setAttribute("aria-selected", false);

      this.input.setAttribute("aria-activedescendant", "");
      this.removeAttribute("loading");
      this.removeAttribute("open");
      this.input.setAttribute("aria-expanded", false);
      this.resultsMaxHeight = false;
      this.predictiveSearchResults?.removeAttribute("style");
    }
  }

  customElements.define("predictive-search", PredictiveSearch);
}

/**
 * Add CSS for highlighting (injected once)
 */
(function injectHighlightStyles() {
  if (document.getElementById("enhanced-search-styles")) return;

  const style = document.createElement("style");
  style.id = "enhanced-search-styles";
  style.textContent = `
    mark.enhanced-search-highlight,
    .enhanced-search-highlight {
      background-color: rgba(255, 235, 59, 0.4) !important;
      color: inherit !important;
      padding: 0 2px !important;
      border-radius: 2px;
      font-weight: inherit;
      display: inline;
    }
  `;
  document.head.appendChild(style);
})();

// Log config for debugging
console.log("[Enhanced Search] Config:", {
  active: ENHANCED_SEARCH_CONFIG.active,
  clientId: ENHANCED_SEARCH_CONFIG.clientId,
  highlightEnabled: ENHANCED_SEARCH_CONFIG.highlightEnabled,
});
